import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import { getBooking, getBookingCoaching, generateCoaching, submitFeedback } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Lightbulb,
  AlertTriangle,
  RefreshCw,
  Star,
  Loader2,
  CheckCircle2,
  FileText,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating, onSelect }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <button
        key={i}
        onClick={() => onSelect(i)}
        className={`p-2 rounded-lg transition-colors ${
          rating >= i ? 'text-amber-500' : 'text-slate-300 hover:text-slate-400'
        }`}
        data-testid={`rating-star-${i}`}
      >
        <Star className={`h-6 w-6 ${rating >= i ? 'fill-current' : ''}`} />
      </button>
    );
  }
  return <div className="flex gap-2">{stars}</div>;
}

function TipsList({ tips, mode }) {
  if (!tips || tips.length === 0) return null;
  const items = [];
  
  // Check if this is unknown customer mode (simple tips)
  const isUnknownMode = mode === 'unknown_customer_disc';
  
  for (let i = 0; i < tips.length; i++) {
    const tip = tips[i];
    
    // Handle different tip formats
    const isStructured = typeof tip === 'object' && tip.title;
    const isSimpleTip = typeof tip === 'object' && tip.tip;
    const isString = typeof tip === 'string';
    
    if (isUnknownMode || isSimpleTip) {
      // Simple format for unknown customer DISC
      const tipText = isSimpleTip ? tip.tip : (isString ? tip : tip.title || '');
      items.push(
        <div 
          key={i}
          className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0"
          data-testid={`tip-${i + 1}`}
        >
          <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-sm font-bold">
            {i + 1}
          </span>
          <p className="text-slate-700 leading-relaxed">{tipText}</p>
        </div>
      );
    } else if (isStructured) {
      // Structured format for matched DISC
      items.push(
        <div 
          key={i}
          className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden"
          data-testid={`tip-${i + 1}`}
        >
          <div className="bg-slate-900 px-4 py-2 flex items-center gap-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-500 text-white text-sm font-bold">
              {i + 1}
            </span>
            <span className="font-medium text-white">
              {tip.title}
            </span>
          </div>
          
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-16 text-xs font-semibold uppercase tracking-wider text-emerald-600 pt-0.5">
                Gør
              </span>
              <p className="text-slate-700">{tip.do}</p>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-16 text-xs font-semibold uppercase tracking-wider text-blue-600 pt-0.5">
                Sig
              </span>
              <p className="text-slate-700 italic">"{tip.say}"</p>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-16 text-xs font-semibold uppercase tracking-wider text-red-500 pt-0.5">
                Undgå
              </span>
              <p className="text-slate-500">{tip.avoid}</p>
            </div>
            
            <div className="flex items-start gap-3 pt-2 border-t border-slate-100">
              <span className="flex-shrink-0 w-16 text-xs font-semibold uppercase tracking-wider text-slate-400 pt-0.5">
                Hvorfor
              </span>
              <p className="text-slate-400 text-sm">{tip.why}</p>
            </div>
          </div>
        </div>
      );
    } else {
      // Fallback string format
      items.push(
        <div 
          key={i}
          className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0"
          data-testid={`tip-${i + 1}`}
        >
          <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-sm font-bold">
            {i + 1}
          </span>
          <p className="text-slate-700 leading-relaxed">{tip}</p>
        </div>
      );
    }
  }
  
  // Different container styling based on mode
  if (isUnknownMode) {
    return <div className="bg-white rounded-xl border border-slate-100 p-4">{items}</div>;
  }
  
  return <div className="space-y-4">{items}</div>;
}

function RisksList({ risks }) {
  if (!risks || risks.length === 0) return null;
  const items = [];
  for (let i = 0; i < risks.length; i++) {
    items.push(
      <p key={i} className="text-amber-800 text-sm">• {risks[i]}</p>
    );
  }
  return (
    <Card className="border-amber-100 bg-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Vær opmærksom på
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">{items}</CardContent>
    </Card>
  );
}

const BookingDetailPage = () => {
  const { hubhusBookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'info');
  
  const [feedbackUsed, setFeedbackUsed] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  // Asset processing URL state
  const [assetUrl, setAssetUrl] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [hubhusBookingId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const bookingRes = await getBooking(hubhusBookingId);
      setBooking(bookingRes.data);
      
      // If it's a montering booking, check for asset_processing_url
      if (bookingRes.data?.booking_type === 'montering') {
        // First check if URL is already on booking
        if (bookingRes.data?.asset_processing_url) {
          setAssetUrl(bookingRes.data.asset_processing_url);
        } else {
          // Try to fetch from backend
          loadAssetUrl();
        }
      }
      
      try {
        const coachingRes = await getBookingCoaching(hubhusBookingId);
        setCoaching(coachingRes.data);
      } catch (e) {
        setCoaching(null);
      }
    } catch (error) {
      console.error('Failed to load booking:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAssetUrl = async () => {
    setAssetLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bookings/${hubhusBookingId}/asset-processing`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.asset_processing_url) {
        setAssetUrl(response.data.asset_processing_url);
      }
    } catch (error) {
      console.error('Failed to load asset URL:', error);
    } finally {
      setAssetLoading(false);
    }
  };

  const handleGenerateCoaching = async () => {
    setCoachingLoading(true);
    try {
      const response = await generateCoaching(hubhusBookingId);
      setCoaching(response.data);
      setActiveTab('coaching');
    } catch (error) {
      console.error('Failed to generate coaching:', error);
    } finally {
      setCoachingLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    setFeedbackLoading(true);
    try {
      await submitFeedback(hubhusBookingId, {
        used: feedbackUsed,
        rating: feedbackRating > 0 ? feedbackRating : null,
        note: feedbackNote || null
      });
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    try {
      return format(parseISO(isoString), "EEEE 'd.' d MMMM 'kl.' HH:mm", { locale: da });
    } catch (e) {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="h-48 w-full mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
        <p className="text-slate-500">Booking ikke fundet</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 font-outfit">
              {booking.customer_name}
            </h1>
            <p className="text-sm text-slate-500">
              {formatDateTime(booking.scheduled_start)}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="info" data-testid="tab-info">Kundeinfo</TabsTrigger>
            <TabsTrigger value="coaching" data-testid="tab-coaching">
              Coaching
              {coaching && <CheckCircle2 className="ml-1 h-4 w-4 text-teal-500" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4">
            <Card className="border-slate-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium text-slate-700">
                  Kundeoplysninger
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Clock className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Tidspunkt</p>
                    <p className="font-medium text-slate-900">
                      {formatDateTime(booking.scheduled_start)}
                    </p>
                  </div>
                </div>

                {booking.address_line && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Adresse</p>
                      <p className="font-medium text-slate-900">
                        {booking.address_line}
                        {booking.postal_code && <br />}
                        {booking.postal_code}
                        {booking.city && ` ${booking.city}`}
                      </p>
                    </div>
                  </div>
                )}

                {booking.customer_phone && (
                  <a 
                    href={`tel:${booking.customer_phone}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <Phone className="h-5 w-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Telefon</p>
                      <p className="font-medium text-teal-700">{booking.customer_phone}</p>
                    </div>
                  </a>
                )}

                {booking.customer_email && (
                  <a 
                    href={`mailto:${booking.customer_email}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <Mail className="h-5 w-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium text-teal-700">{booking.customer_email}</p>
                    </div>
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Asset Processing Button for Montering bookings */}
            {booking.booking_type === 'montering' && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-900">Ordredokument</p>
                        <p className="text-sm text-blue-700">Se kundens ordredetaljer</p>
                      </div>
                    </div>
                    {assetLoading ? (
                      <Button disabled className="bg-blue-600">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Henter...
                      </Button>
                    ) : assetUrl ? (
                      <Button 
                        onClick={() => window.open(assetUrl, '_blank')}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="asset-processing-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Åbn dokument
                      </Button>
                    ) : (
                      <Button 
                        onClick={loadAssetUrl}
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        data-testid="fetch-asset-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Hent dokument
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Badge className={
                booking.survey_status === 'received' 
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                  : 'bg-amber-50 text-amber-700 border-amber-100'
              }>
                Survey: {booking.survey_status === 'received' ? 'Modtaget' : 'Afventer'}
              </Badge>
              <Badge className={
                booking.coaching_status === 'ready' 
                  ? 'bg-teal-50 text-teal-700 border-teal-100' 
                  : 'bg-slate-100 text-slate-600'
              }>
                Coaching: {booking.coaching_status === 'ready' ? 'Klar' : 'Afventer'}
              </Badge>
            </div>
          </TabsContent>

          <TabsContent value="coaching" className="space-y-4">
            {!coaching ? (
              <Card className="border-slate-100 border-dashed">
                <CardContent className="p-8 text-center">
                  <Lightbulb className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Coaching ikke genereret endnu</p>
                  <Button 
                    onClick={handleGenerateCoaching}
                    disabled={coachingLoading}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="generate-coaching-btn"
                  >
                    {coachingLoading ? (
                      <span className="flex items-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Genererer...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Generér coaching
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Header with heading and subheading */}
                <div className="bg-slate-900 rounded-xl p-5">
                  <h2 className="text-white font-semibold text-lg">
                    {coaching.heading || coaching.opening_line || 'Forberedelse til dit besøg'}
                  </h2>
                  {coaching.subheading && (
                    <p className="text-slate-300 text-sm mt-1">{coaching.subheading}</p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Badge className="bg-slate-700 text-slate-200 text-xs border-0">
                      {coaching.coaching_source === 'survey' ? 'Baseret på survey' : 'Generelle råd'}
                    </Badge>
                    {coaching.mode === 'matched_disc' && coaching.customer_disc_guess && (
                      <Badge className="bg-teal-600 text-white text-xs border-0">
                        Kunde analyseret
                      </Badge>
                    )}
                  </div>
                </div>

                <TipsList tips={coaching.tips_json} mode={coaching.mode} />
                <RisksList risks={coaching.risks_json} />

                <Button 
                  variant="outline" 
                  onClick={handleGenerateCoaching}
                  disabled={coachingLoading}
                  className="w-full"
                  data-testid="regenerate-coaching-btn"
                >
                  {coachingLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Generér ny coaching
                </Button>

                <Card className="border-slate-100 mt-6">
                  <CardHeader>
                    <CardTitle className="text-base font-medium text-slate-700">
                      Feedback
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {feedbackSubmitted ? (
                      <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle2 className="h-5 w-5" />
                        <span>Tak for din feedback!</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="used" className="text-slate-600">
                            Brugte du disse råd?
                          </Label>
                          <Switch 
                            id="used"
                            checked={feedbackUsed}
                            onCheckedChange={setFeedbackUsed}
                            data-testid="feedback-used-switch"
                          />
                        </div>

                        <div>
                          <Label className="text-slate-600 mb-2 block">Rating</Label>
                          <StarRating rating={feedbackRating} onSelect={setFeedbackRating} />
                        </div>

                        <div>
                          <Label htmlFor="note" className="text-slate-600 mb-2 block">
                            Kommentar (valgfri)
                          </Label>
                          <Textarea 
                            id="note"
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            placeholder="Skriv evt. en kommentar..."
                            rows={3}
                            data-testid="feedback-note-input"
                          />
                        </div>

                        <Button 
                          onClick={handleSubmitFeedback}
                          disabled={feedbackLoading}
                          className="w-full bg-slate-900 hover:bg-slate-800"
                          data-testid="submit-feedback-btn"
                        >
                          {feedbackLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Send feedback
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default BookingDetailPage;
